// library/model — the Library's (Bibliotek) Model: what the module owns
// beside the frame. The query text and the opened row are FRAME state (the
// active Search tab, mirrored to the URL); the module keeps the last values it
// was shown so it can tell what changed, plus everything that is its own:
// the marked row, the browse order, and the two remote reads (the content
// search for the query's free text, the opened item's own content).
//
// Pure data. Every remote value is a `Remote` (no nullable value + error pair).

import type { NewType } from "@bilda/utils";
import { Remote } from "@bilda/utils";
import type { ExternalError } from "../../app/externalError.js";
import type { LoadedExport } from "../../app/ports/packaging.js";
import type { LoadedFile, LoadedSource } from "../../app/ports/resource.js";
import type { ContentSearch } from "../../components/Search/contentSearch.js";
import type { SearchResultKind } from "../../components/Search/searchModel.js";
import type {
    MaterialSortField,
    SourceSortField,
    TabSort,
} from "../../components/SearchPage/searchRows.js";

// The row the author has gone into, as the frame shows it.
export type Opened = {
    readonly id: string;
    readonly kind: SearchResultKind;
};

// The kinds whose item view the module loads. A material is edited in place
// through the editor host (its own load); a conversation lives in the chat.
export type LoadedKind = "source" | "package" | "file";

export type ItemContent =
    | NewType<"Item.Source", { readonly source: LoadedSource }>
    | NewType<"Item.Package", { readonly pkg: LoadedExport }>
    | NewType<"Item.File", { readonly file: LoadedFile }>;

// The opened item's own content: nothing to load (the list, a material), or
// one item's read.
export type Detail =
    | NewType<"Detail.Idle">
    | NewType<
          "Detail.Open",
          {
              readonly id: string;
              readonly kind: LoadedKind;
              readonly load: Remote.Remote<ItemContent, ExternalError>;
          }
      >;

// Where the author is in the Search list (pen Q82): how many pages of
// {results} show and where the list was scrolled when an item was opened,
// for the one list `key` names (its tab, field and sort). Another list
// starts from the top; coming back to this one lands in the same place.
export type ListPlace = {
    readonly key: string;
    readonly pages: number;
    readonly scrollTop: number;
};

export const listPlaceOf = (model: LibraryModel, key: string): ListPlace =>
    model.list.key === key ? model.list : { key, pages: 1, scrollTop: 0 };

// The sort the author chose on each tab (null: the tab's default).
type ListSorts = {
    readonly materials: TabSort<MaterialSortField> | null;
    readonly sources: TabSort<SourceSortField> | null;
};

export type LibraryModel = {
    // The frame's query text and opened row, as last shown.
    readonly query: string;
    readonly openedId: string | null;
    // The row the arrow keys moved to, or the one last gone into (marked on
    // the way back).
    readonly marked: string | null;
    // The content search (C3) for the query's free words.
    readonly content: ContentSearch;
    readonly detail: Detail;
    readonly list: ListPlace;
    readonly sorts: ListSorts;
    // How many times Search was summoned (⌘K, the menu): each one focuses
    // its field.
    readonly summons: number;
};

export const initialLibraryModel: LibraryModel = {
    query: "",
    openedId: null,
    marked: null,
    content: { words: [], hits: Remote.notAsked },
    detail: { _tag: "Detail.Idle" },
    list: { key: "", pages: 1, scrollTop: 0 },
    sorts: { materials: null, sources: null },
    summons: 0,
};
